import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { StyledTableCell } from "../../../../ui-components/StyledTables";
import { useSuperAdminSingleOrg } from "../../../../context/SuperAdmin/SingleOrgContext";
import SortedHeaderCell from "./../SortedHeaderCell";
import SingleOrgPagination from "./../SingleOrgPagination";
import { UserOrgColumn } from "../../../../interfaces/SuperAdmin/Pages/iDataUserOrganization";
import { iDataUser } from "../../../../interfaces/SuperAdmin/Pages/iDataUser";
import { useAuthContext } from "../../../../hooks/Auth";
import RowUser from "./RowUser";

export const SingleOrgUsersTable = () => {
  const [orderBy, setOrderBy] = useState<UserOrgColumn | null>(null);
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");
  const [sortedUsers, setSortedUsers] = useState<iDataUser[]>([]);
  const [paginatedUsers, setPaginatedUsers] = useState<iDataUser[]>([]);

  const {
    handleChangePage,
    handleChangeRowsPerPage,
    usuarios,
    page,
    rowsPerPage,
    assignToDeleteUser,
    handleEditUser,
    userSearchQuery,
  } = useSuperAdminSingleOrg();

  const { canAccess } = useAuthContext();

  // Filtro de usuarios por búsqueda
  const filteredUsers = useMemo(() => {
    const normalizedSearch = userSearchQuery.toLowerCase().trim();
    return usuarios.filter(
      (u) =>
        u.nombres?.toLowerCase().includes(normalizedSearch) ||
        u.apellidos?.toLowerCase().includes(normalizedSearch) ||
        u.email?.toLowerCase().includes(normalizedSearch)
    );
  }, [usuarios, userSearchQuery]);

  useEffect(() => {
    // Ordenar usuarios basado en `orderBy` y `orderDirection`
    setSortedUsers(
      [...filteredUsers].sort((a, b) => {
        if (!orderBy) return 0;

        let aValue;
        let bValue;

        switch (orderBy) {
          case "fecha":
            aValue = new Date(a.created_at);
            bValue = new Date(b.created_at);
            break;
          case "estado":
            aValue = a.estado;
            bValue = b.estado;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return orderDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return orderDirection === "asc" ? 1 : -1;
        return 0;
      })
    );
  }, [filteredUsers, orderBy, orderDirection]);

  useEffect(() => {
    setPaginatedUsers(
      sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    );
  }, [page, sortedUsers, rowsPerPage]);

  // Función para manejar el cambio de orden
  const handleSort = (column: UserOrgColumn) => {
    setOrderBy((prev) => (prev === column ? null : column));
    setOrderDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const isNotProfessor = canAccess("edit:users") && canAccess("delete:users");

  return (
    <>
      <TableContainer
        sx={{
          width: "100%",
          margin: "auto",
          borderRadius: 2,
          overflow: "hidden",
          mt: 2,
        }}
      >
        <Table sx={{ minWidth: 500, width: "100%" }} aria-label="simple table">
          {/* Cabecera de la tabla */}
          <TableHead sx={{ height: 20 }}>
            <TableRow>
              <StyledTableCell>N°</StyledTableCell>
              <StyledTableCell>Nombre</StyledTableCell>
              <StyledTableCell>Apellido</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <SortedHeaderCell
                label="Fecha de creación"
                column="fecha"
                handleSort={handleSort}
                orderBy={orderBy}
                orderDirection={orderDirection}
              />
              <SortedHeaderCell
                label="Estado"
                column="estado"
                handleSort={handleSort}
                orderBy={orderBy}
                orderDirection={orderDirection}
              />
              {isNotProfessor && (
                <StyledTableCell align="center">Acciones</StyledTableCell>
              )}
            </TableRow>
          </TableHead>

          {/* Cuerpo de la tabla */}
          <TableBody>
            {paginatedUsers.length ? (
              paginatedUsers.map((usuario, index) => (
                <RowUser
                  key={usuario.id_usuario}
                  usuario={usuario}
                  index={index}
                  assignToDeleteUser={assignToDeleteUser}
                  handleEdit={handleEditUser}
                />
              ))
            ) : (
              <TableRow>
                <StyledTableCell
                  colSpan={9}
                  sx={{
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    textAlign: "center",
                  }}
                >
                  No hay campos aún
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <SingleOrgPagination
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        count={usuarios.length}
      />
    </>
  );
};
